import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Lightbulb, Send, Loader2, ThumbsUp, ThumbsDown, Sparkles, Globe2 } from "lucide-react";
import { Header } from "@/components/Header";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ["Student abroad", "Professional", "Entrepreneur", "Academic / Researcher", "Diaspora", "Retired expert", "Other"];

const CATS = {
  do: { label: "What we should DO", chip: "DO", color: "#16a34a", bg: "rgba(22,163,74,0.12)", icon: ThumbsUp },
  dont: { label: "What we should AVOID", chip: "AVOID", color: "#dc2626", bg: "rgba(220,38,38,0.12)", icon: ThumbsDown },
  idea: { label: "A big idea", chip: "IDEA", color: "#C77D2E", bg: "var(--brass-light)", icon: Sparkles },
};

const IdeaCard = ({ idea }) => {
  const c = CATS[idea.category] || CATS.idea;
  const meta = [idea.name, idea.role, idea.country].filter(Boolean).join(" · ");
  return (
    <div data-testid={`idea-card-${idea.id}`} className="rounded-3xl p-5 break-inside-avoid" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
      <span className="inline-flex items-center gap-1.5 font-ui text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: c.bg, color: c.color }}>
        <c.icon size={12} /> {c.chip}
      </span>
      {idea.title && <h3 className="font-en text-lg font-bold mt-3" style={{ color: "var(--text)" }}>{idea.title}</h3>}
      <p className="font-ui text-sm mt-2 leading-relaxed whitespace-pre-line" style={{ color: "var(--text-2)" }}>{idea.body}</p>
      {meta && <p className="font-ui text-xs mt-3" style={{ color: "var(--text-muted)" }}>— {meta}</p>}
    </div>
  );
};

export default function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", country: "", category: "idea", title: "", body: "" });

  const load = () => axios.get(`${API}/ideas`).then((r) => setIdeas(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.body.trim().length < 10) { toast.error("Please share a little more detail."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ideas`, form);
      toast.success(data.message || "Thank you 🙏");
      setForm({ name: "", role: "", country: "", category: "idea", title: "", body: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  const shown = filter === "all" ? ideas : ideas.filter((i) => i.category === filter);

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/rooms/sindu-kamare" data-testid="ideas-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft size={16} /> Back
        </Link>

        {/* Hero */}
        <div className="text-center mb-8">
          <span className="grid place-items-center w-16 h-16 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Lightbulb size={30} /></span>
          <h1 className="font-en text-4xl sm:text-5xl font-extrabold mt-4" style={{ color: "var(--text)" }}>Ideas for Sri Lanka</h1>
          <p className="font-ui text-base mt-3 max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--text-2)" }}>
            A stage for every Sri Lankan — students abroad, professionals, the diaspora — who has a voice but no one to listen. Share how our nation can rise to developed status: <b style={{ color: "var(--text)" }}>what we should do, and what we should avoid.</b>
          </p>
          <p className="font-ui text-sm mt-2" style={{ color: "var(--text-muted)" }}><Globe2 size={13} className="inline mb-0.5" /> Open to all · in English · every idea reviewed before it is shown</p>
        </div>

        {/* Share button */}
        {!open && (
          <div className="text-center mb-8">
            <button onClick={() => setOpen(true)} data-testid="ideas-write-btn" className="inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full font-ui font-bold text-base transition-transform active:scale-95 hover:scale-[1.02]" style={{ background: "var(--accent)", color: "#fff" }}>
              <Lightbulb size={18} /> Share your idea
            </button>
          </div>
        )}

        {/* Composer */}
        {open && (
          <div className="rounded-3xl p-5 sm:p-6 mb-10" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="ideas-composer">
            <div className="grid sm:grid-cols-3 gap-3">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name (optional)" data-testid="ideas-name" className="h-12 px-4 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
              <select value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="ideas-role" className="h-12 px-3 rounded-xl font-ui text-sm outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }}>
                <option value="">Your role (optional)</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Country (optional)" data-testid="ideas-country" className="h-12 px-4 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="ideas-category" className="h-12 px-3 rounded-xl font-ui text-sm outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }}>
                <option value="idea">A big idea</option>
                <option value="do">What we should DO</option>
                <option value="dont">What we should AVOID</option>
              </select>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="A short title (optional)" data-testid="ideas-title" className="sm:col-span-2 h-12 px-4 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            </div>
            <textarea value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Share your idea for Sri Lanka…" rows={5} data-testid="ideas-body" className="w-full mt-3 p-4 rounded-xl font-ui outline-none resize-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            <div className="flex gap-2 mt-3">
              <button onClick={submit} disabled={busy} data-testid="ideas-submit" className="flex-1 h-12 rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Send for review
              </button>
              <button onClick={() => setOpen(false)} className="h-12 px-5 rounded-xl font-ui font-bold" style={{ background: "var(--bg)", color: "var(--text)", border: "2px solid var(--border)" }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 justify-center mb-6" data-testid="ideas-filters">
          {[["all", "All"], ["do", "Do"], ["dont", "Avoid"], ["idea", "Ideas"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`ideas-filter-${k}`} className="h-9 px-4 rounded-full font-ui font-bold text-xs" style={{ background: filter === k ? "var(--accent)" : "var(--card)", color: filter === k ? "#fff" : "var(--text-2)", border: "1px solid var(--border)" }}>{l}</button>
          ))}
        </div>

        {/* Wall */}
        {shown.length === 0 ? (
          <p className="text-center font-ui text-sm py-10" style={{ color: "var(--text-muted)" }} data-testid="ideas-empty">
            No ideas here yet. Be the first to share your voice for Sri Lanka. 🇱🇰
          </p>
        ) : (
          <div className="columns-1 sm:columns-2 gap-4 space-y-4" data-testid="ideas-wall">
            {shown.map((i) => <IdeaCard key={i.id} idea={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
